import { Component, EntityKind, type EntityState, type SnapshotRecord } from "odyssey-shared";

/**
 * The names of the players a client's view holds (ADR-067), for the game report, which a browser scenario
 * reads to check that two clients each hear the other's first and family names.
 */

/**
 * One player's name as the report lists it: its entity's handle and its two parts, apart.
 */
export interface PlayerName {
  readonly handle: number;
  readonly firstName: string;
  readonly familyName: string;
}

/**
 * The names of the players in a view, in handle order. A thing has no name and is left out; a player whose
 * name the client does not hold lists two empty parts.
 *
 * @param entities The view's entities by handle.
 * @returns One entry per player.
 */
export function playerNames(entities: ReadonlyMap<number, EntityState>): PlayerName[] {
  const names: PlayerName[] = [];
  for (const state of entities.values()) {
    if (state.kind === EntityKind.Player) {
      names.push({ handle: state.handle, firstName: state.name.first, familyName: state.name.family });
    }
  }
  return names.sort((a, b) => a.handle - b.handle);
}

/**
 * The names of the players the page draws, which is what a player may be offered by name: a player the view holds but
 * the page does not draw (outside the vision cap, in the server's margin) is left out, and so is one whose world UI is
 * hidden, under a roof the viewer is not under or behind a prop. Offering either would tell the player that someone
 * they cannot see is near.
 *
 * @param names The names of the players the view holds.
 * @param drawn The characters the page draws this frame by handle, each with whether its world UI is hidden.
 * @returns The names kept, in the order given, in a list of their own.
 * @example
 * drawnPlayerNames(gameReport.players, scene.characters);
 */
export function drawnPlayerNames(names: readonly PlayerName[], drawn: ReadonlyMap<number, { readonly hidden: boolean }>): PlayerName[] {
  return names.filter((name) => drawn.get(name.handle)?.hidden === false);
}

/**
 * Whether an applied snapshot could have changed the names its view holds: a keyframe, which replaces the
 * view, a removal, or a record carrying Spawn or the Name. Every other snapshot leaves them as they were, so
 * the report is rebuilt only when this holds.
 *
 * @param record The snapshot.
 * @returns True when the names may differ from before it.
 */
export function mayRename(record: SnapshotRecord): boolean {
  if (record.keyframe || record.removed.length > 0) {
    return true;
  }
  for (const entry of record.entities) {
    if ((entry.flags & (Component.Spawn | Component.Name)) !== 0) {
      return true;
    }
  }
  return false;
}
